<template>
  <ul v-if="state.isIncorePayment && state.incoreQuote" class="p-2 text-sm text-yapgray-600 dark:text-yapgray-200">
    <li class="flex items-center justify-between">
      <div class="flex items-center gap-2">
        <YapIcon name="arrows-up-down-outline" class="size-4" />
        {{ t('webapp.payments.incore.fee.text') }}
      </div>

      <YapAmount :yap-money="state.incoreQuote.additionalFee" class="text-right" />
    </li>
    <li class="flex items-center justify-between">
      <div class="ml-6 flex items-center gap-2">
        {{ t('app.payments.incore.fxRate.label') }}
      </div>
      <span>{{ state.fxRateToChf }}</span>
    </li>
    <li class="flex items-center justify-between">
      <div class="ml-6 flex items-center gap-2">
        {{ t('app.payments.incore.totalAmount.text') }}
        <YapTooltip
          :text="t('app.payments.incore.fee.disclaimer')"
          class="xml-2 min-w-64 -translate-y-4 translate-x-1/2"
        >
          <YapIcon name="info-outline" class="size-4 text-yapgray-500 dark:text-yapgray-500" />
        </YapTooltip>
      </div>
      <YapAmount :yap-money="state.incoreQuote.sourceAmount" class="text-right" />
    </li>
  </ul>
</template>

<script setup lang="ts">
import { isValidIBAN } from 'ibantools';
import { watchDebounced } from '@vueuse/core';
import { reactive, computed, onMounted } from 'vue';

import apiGw from '@/services/apiGw';
import { useI18n } from '@/plugins/i18n';
import YapIcon from '@/components/YapIcon.vue';
import YapAmount from '@/components/YapAmount.vue';
import YapTooltip from '@/components/YapTooltip.vue';
import { useSubscriptionsStore } from '@/store/subscriptionsStore';
import { IncoreQuote } from '@/components/payments/types/incoreQuote';
import { PlanFeatures } from '@/components/billing/types/subscription';
import { stringToYapAmount } from '@/utils/general';
import { isYapealIban } from '@/utils/formats';
import { formatFxRate } from '@/utils/locale';

const props = defineProps<{
  amount: string;
  sourceCurrency?: string;
  targetCurrency: string;
  targetIban?: string;
  sourceIban?: string;
}>();

const emit = defineEmits(['incoreQuoteLoaded']);

interface State {
  incoreQuote?: IncoreQuote;
  fxRateToChf?: string;
  isIncorePayment: boolean;
  isInternalPayment: boolean;
  hasFeatureInternationalPaymentSEPAOut: boolean;
  hasFeatureInternationalPaymentSWIFTOut: boolean;
}

const state: State = reactive({
  incoreQuote: undefined,
  fxRateToChf: computed(() => {
    if (state.incoreQuote) {
      return formatFxRate(state.incoreQuote);
    }
    return undefined;
  }),
  isIncorePayment: computed(() => {
    const iban = props.targetIban;
    return (!!iban && !iban.startsWith('CH') && !iban.startsWith('LI')) || props.targetCurrency !== 'CHF';
  }),
  isInternalPayment: computed(() => {
    const targetIban = props.targetIban;
    const sourceIban = props.sourceIban;
    if (!targetIban || !sourceIban) return false;
    return isYapealIban(targetIban) && isYapealIban(sourceIban);
  }),
  hasFeatureInternationalPaymentSEPAOut: false,
  hasFeatureInternationalPaymentSWIFTOut: false
});

const { t } = useI18n();
const subscriptionsStore = useSubscriptionsStore();

onMounted(async () => {
  state.hasFeatureInternationalPaymentSEPAOut = await subscriptionsStore.currentContextHasFeature(
    PlanFeatures.FeatureInternationalPaymentSEPAOut
  );
  state.hasFeatureInternationalPaymentSWIFTOut = await subscriptionsStore.currentContextHasFeature(
    PlanFeatures.FeatureInternationalPaymentSWIFTOut
  );
  loadIncoreQuoteIfNeeded();
});

watchDebounced(
  () => [props.amount, props.targetCurrency, props.targetIban, props.sourceIban],
  async (newVal, oldVal) => {
    if (newVal && newVal !== oldVal) {
      loadIncoreQuoteIfNeeded();
    }
  },
  { debounce: 500 }
);

async function loadIncoreQuoteIfNeeded() {
  if (!state.hasFeatureInternationalPaymentSEPAOut && !state.hasFeatureInternationalPaymentSWIFTOut) {
    return;
  }
  if (
    state.isInternalPayment
      ? props.targetCurrency !== props.sourceCurrency
      : !!props.targetIban && isValidIBAN(props.targetIban) && state.isIncorePayment && !!props.targetCurrency
  ) {
    const resp = await apiGw.post('/v2/incore/quote/calculate', {
      targetAmount: stringToYapAmount(props.amount),
      sourceCurrency: 'CHF',
      targetCurrency: props.targetCurrency,
      targetIban: props.targetIban,
      sourceIban: props.sourceIban
    });
    state.incoreQuote = resp.data;
    emit('incoreQuoteLoaded', state.incoreQuote);
  } else {
    state.incoreQuote = undefined;
    emit('incoreQuoteLoaded', undefined);
  }
}
</script>
